import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  ModuleTitle,
  Table,
  TableMenu,
  UserSelect,
} from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { format, parseISO } from 'date-fns'
import { Field, Header } from '@/modules/companies'
import React from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { COMPANY_CHECKLISTS } from '../queries'
import type { AllChecklistsQuery } from '../types.graphql'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PageType } from '@/modules/common/consts'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

const Filter = styled(Card)`
  display: flex;
  gap: 1rem;
  flex-direction: row;
  margin-bottom: 1rem;
`

const CheckboxField = styled(Field)`
  display: flex;
  align-items: left;

  & > div {
    justify-content: flex-start;
    padding-left: 1rem;
  }
`

export const Checklists: React.FC = () => {
  const translations = useTranslate({
    user: 'common.user',
    status: 'common.status',
    name: 'common.name',
    noResponse: 'common.no-response',
    noResults: 'common.no-results',
    date: 'common.date',
    onlyShowChecklistsWithoutResponse:
      'checklists.only-show-checklists-without-response',
    checklists: 'common.checklists',
    view: 'permissions.view',
    responseTimestamp: 'common.response-timestamp',
  })

  const navigate = useNavigate()

  const { formValues, updateForm } = useForm<{
    user: string | null
    date: Date | null
    noResponseOnly: boolean
  }>({
    values: {
      user: null,
      date: new Date(),
      noResponseOnly: false,
    },
  })

  const { InfoModal, infoContent, InfoButton } = useInfoModal(
    PageType.CHECKLISTS
  )

  const { data, loading } = useQuery<AllChecklistsQuery>(COMPANY_CHECKLISTS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      user: formValues.user,
      date: formValues.date
        ? format(formValues.date, 'yyyy-MM-dd')
        : new Date(),
      noResponseOnly: formValues.noResponseOnly ? true : null,
    },
  })
  return (
    <Wrapper>
      <Header>
        <ModuleTitle>
          {translations.checklists}

          {infoContent && <InfoButton />}
        </ModuleTitle>
      </Header>

      <Filter>
        <Field>
          <label>{translations.user}</label>
          <UserSelect
            value={formValues.user}
            onChange={value => updateForm({ user: value })}
            nullable
            grayed
            width="400px"
          />
        </Field>

        <Field>
          <label>{translations.date}</label>
          <DatePicker
            value={formValues.date ?? new Date()}
            onChange={value => updateForm({ date: value })}
            grayed
            clearable
          />
        </Field>

        <CheckboxField>
          <label>{translations.onlyShowChecklistsWithoutResponse}</label>
          <Checkbox
            boxSize="54px"
            checked={formValues.noResponseOnly}
            onChange={value => updateForm({ noResponseOnly: value })}
            grayed
          />
        </CheckboxField>
      </Filter>

      <Table
        noDataText={translations.noResults}
        noData={data?.allChecklists.edges.length === 0 && !loading}
      >
        <thead>
          <tr>
            <th>{translations.date}</th>
            <th>{translations.responseTimestamp}</th>
            <th>{translations.name}</th>
            <th>{translations.user}</th>
            <th>{translations.status}</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {data?.allChecklists.edges.map(edge => {
            const node = edge.node
            const createdAt = parseISO(node.createdAt)
            const updatedAt = parseISO(node.updatedAt)

            return (
              <tr key={node.id}>
                <td>{format(createdAt, 'dd.MM.yyyy')}</td>
                <td>{node.active ? '-' : format(updatedAt, 'HH:mm')}</td>
                <td>{node.title}</td>
                <td>{node.user.fullName}</td>
                <td>{!node.active ? 'OK' : translations.noResponse}</td>
                <td width="1px">
                  <TableMenu
                    items={[
                      {
                        label: translations.view,
                        onClick: () => void navigate(`/checklists/${node.id}`),
                      },
                    ]}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
